import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import FullBlockingModal from '../common/FullBlockingModal';
import { useQuoteStore } from '../../stores/quoteStore';
import { Quote } from '../../types/quote';

interface QuoteConfirmRetireModalProps {
  quote: Quote;
  onClose: () => void;
  onActivated?: () => void;
}

const QuoteConfirmActivateModal = ({
  quote,
  onClose,
  onActivated,
}: QuoteConfirmRetireModalProps) => {
  const { t } = useTranslation(['quotes', 'common']);
  const { activateQuote } = useQuoteStore();

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleActivate = async () => {
    try {
      setProcessing(true);
      setError(null);

      await activateQuote(quote.id);
      onActivated?.();
      onClose();
    } catch (err) {
      console.error(err);
      setError(
        t('quoteActivateError', {
          ns: 'quotes',
          defaultValue: 'Erreur lors de l’activation de la soumission.',
        })
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[48rem]">
      <button
        className="absolute right-4 top-4 text-2xl text-gray-400 hover:text-gray-600 md:top-0"
        onClick={onClose}
        aria-label={t('common:form_actions.close')}
      >
        &times;
      </button>

      <h2 className="mb-4 text-2xl font-bold text-secondary">
        {t('confirmActivateTitle', { ns: 'quotes' })}
      </h2>

      <p className="font-ambit mb-6 text-[0.9375rem] text-secondary">
        {t('confirmActivateMessage', {
          ns: 'quotes',
          defaultValue:
            'Voulez-vous vraiment activer votre soumission pour le projet : {{jobTitle}} ?',
          jobTitle: quote.job?.title,
        })}
      </p>

      {error && <div className="mb-4 text-[#F04438]">{error}</div>}

      <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
        <Button
          className="w-full bg-primary text-white md:w-auto md:px-8"
          onClick={handleActivate}
          disabled={processing}
        >
          {processing
            ? t('activating', { ns: 'quotes', defaultValue: 'Activation en cours...' })
            : t('confirmActivateButton', { ns: 'quotes', defaultValue: 'Activer' })}
        </Button>

        <Button
          className="w-full md:w-auto md:px-8"
          variant="transparent-with-border"
          onClick={onClose}
        >
          {t('common:form_actions.cancel')}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default QuoteConfirmActivateModal;
